// /pack/:packId — pack scene + practice CTA. Step 8 lifts this from the
// step-6 stepping-stone into the real scene canvas. Step 9 will add the
// inventory link once there are unlocked-but-not-colored items to color.

import { useEffect, useState } from 'react'
import { Link, useParams, Navigate, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { isPackId, PACKS, type PackId } from '@skena/schema'
import { useAuthStore } from '../auth/store.ts'
import { fetchPackScene } from '../scene/sceneApi.ts'
import { PackScene } from '../scene/PackScene.tsx'
import { CapstoneSection } from '../scene/CapstoneSection.tsx'

const PACK_PAGE_BG: Record<PackId, string> = {
  joseph: 'bg-[var(--color-joseph-bg)]',
  nativity: 'bg-[var(--color-nativity-bg)]',
}
const PACK_ACCENT_TEXT: Record<PackId, string> = {
  joseph: 'text-[var(--color-joseph-primary)]',
  nativity: 'text-[var(--color-nativity-primary)]',
}
const PACK_BUTTON: Record<PackId, string> = {
  joseph:
    'bg-[var(--color-joseph-primary)] text-white hover:-translate-y-0.5',
  nativity:
    'bg-[var(--color-nativity-primary)] text-white hover:-translate-y-0.5',
}
// CTA shadow tinted with pack primary at 35% — matches the surface mood,
// avoids the generic shadow-md grey halo. Inline so we can color-mix.
const PACK_CTA_SHADOW: Record<PackId, string> = {
  joseph: '0 6px 16px color-mix(in oklab, var(--color-joseph-primary) 35%, transparent)',
  nativity: '0 6px 16px color-mix(in oklab, var(--color-nativity-primary) 35%, transparent)',
}

export function PackHomePage() {
  const { packId } = useParams<{ packId: string }>()
  if (!packId || !isPackId(packId)) {
    return <Navigate to="/" replace />
  }
  return <PackHomeInner packId={packId} />
}

function PackHomeInner({ packId }: { packId: PackId }) {
  const user = useAuthStore((s) => s.user)
  const pack = PACKS[packId]
  const [searchParams, setSearchParams] = useSearchParams()
  // Capture-once: ColoringPage hands ?justCompleted=itemId, we read it on
  // mount, then strip the query param so refetches / refreshes don't keep
  // replaying the fly-in animation.
  const [justCompletedItemId, setJustCompletedItemId] = useState<string | null>(null)

  useEffect(() => {
    const param = searchParams.get('justCompleted')
    if (param) {
      setJustCompletedItemId(param)
      const next = new URLSearchParams(searchParams)
      next.delete('justCompleted')
      setSearchParams(next, { replace: true })
    }
  }, [searchParams, setSearchParams])

  const sceneQuery = useQuery({
    queryKey: ['pack-scene', packId],
    queryFn: () => fetchPackScene(packId),
    staleTime: 30 * 1000,
  })

  const items = sceneQuery.data?.items ?? []
  const unlockedCount = items.filter((i) => i.isUnlocked).length
  const completedCount = items.filter((i) => i.isComplete).length
  const pendingColorCount = items.filter((i) => i.isUnlocked && !i.isComplete).length
  const totalCount = pack.itemIds.length

  return (
    <main
      className={`mx-auto flex min-h-full max-w-2xl flex-col gap-6 p-6 ${PACK_PAGE_BG[packId]}`}
      data-pack={packId}
    >
      <header className="flex items-baseline justify-between">
        <Link
          to="/"
          className="text-sm text-[var(--color-muted)] underline-offset-4 hover:underline"
        >
          ← 回主頁
        </Link>
        <p className="text-xs text-[var(--color-muted)]">
          {user?.displayName ? `嗨，${user.displayName}` : ''}
        </p>
      </header>

      <section className="flex flex-col gap-1">
        <p className={`text-xs tracking-wider uppercase ${PACK_ACCENT_TEXT[packId]}`}>
          {pack.scriptureRange}
        </p>
        <h1 className="font-[family-name:var(--font-display)] text-3xl text-[var(--color-fg)]">
          {pack.nameZh}
        </h1>
        <p className="text-sm text-[var(--color-muted)]">{pack.nameEn}</p>
      </section>

      {pack.storyZh ? (
        <section
          aria-label="單元背景"
          className="flex flex-col gap-3 rounded-[var(--radius-soft)] border border-black/5 bg-white/70 p-5"
        >
          <p className="text-base leading-relaxed text-[var(--color-fg)]">
            {pack.storyZh}
          </p>
          {pack.storyEn ? (
            <p className="text-sm italic leading-relaxed text-[var(--color-muted)]">
              {pack.storyEn}
            </p>
          ) : null}
        </section>
      ) : null}

      <section>
        {sceneQuery.isLoading ? (
          <div
            className="grid grid-cols-2 gap-3 sm:grid-cols-3"
            aria-label="loading scene"
          >
            {Array.from({ length: pack.itemIds.length }).map((_, i) => (
              <div
                key={i}
                className="aspect-square animate-pulse rounded-[var(--radius-soft)] bg-black/5"
              />
            ))}
          </div>
        ) : sceneQuery.isError ? (
          <div className="rounded-[var(--radius-soft)] border-2 border-dashed border-[var(--color-decoration)]/40 p-8 text-center text-sm text-[var(--color-muted)]">
            場景載入失敗，等等再回來
          </div>
        ) : (
          <>
            <PackScene
              packId={packId}
              items={items}
              {...(justCompletedItemId ? { justCompletedItemId } : {})}
            />
            <CapstoneSection packId={packId} />
          </>
        )}
      </section>

      <section className="flex items-center justify-between text-sm text-[var(--color-muted)]">
        <span>
          已解鎖 <strong className="text-[var(--color-fg)]">{unlockedCount}</strong> / {totalCount}
        </span>
        <span>
          已上色 <strong className="text-[var(--color-fg)]">{completedCount}</strong> / {totalCount}
        </span>
      </section>

      <div className="flex flex-col gap-3">
        <Link
          to={`/pack/${pack.id}/practice`}
          className={`rounded-[var(--radius-pill)] px-8 py-4 text-center text-lg font-[family-name:var(--font-display)] transition-transform ${PACK_BUTTON[packId]}`}
          style={{
            boxShadow: PACK_CTA_SHADOW[packId],
            fontWeight: 600,
            letterSpacing: '0.05em',
          }}
        >
          <span aria-hidden="true" className="opacity-90">❦</span>
          {' '}開始練習
        </Link>
        {pendingColorCount > 0 ? (
          <Link
            to={`/pack/${pack.id}/inventory`}
            className={`rounded-[var(--radius-pill)] border-2 px-6 py-3 text-center text-base font-semibold transition-transform hover:-translate-y-0.5 ${
              packId === 'joseph'
                ? 'border-[var(--color-joseph-primary)] text-[var(--color-joseph-primary)]'
                : 'border-[var(--color-nativity-primary)] text-[var(--color-nativity-primary)]'
            }`}
          >
            我的線條稿（{pendingColorCount}）→ 去上色
          </Link>
        ) : null}
      </div>

      <p className="text-center text-xs text-[var(--color-muted)]">
        {completedCount === totalCount && totalCount > 0
          ? '全部蒐齊！再去完成全景大獎'
          : completedCount > 0
            ? '繼續加油，蒐齊全部解鎖大獎'
            : '答對題目就會解鎖元素的線條稿'}
      </p>
    </main>
  )
}
